import { magnetPos } from "./sim.js";

// Concept-page diagrams define the canonical mapping: action=blue,
// observation=rose, relation=green. Hex values mirror the suit aliases
// in tokens.css; keep them in sync if those change.
const SUIT_COLORS = {
  action:      "#82C7F5",
  observation: "#D175AC",
  relation:    "#90E9A2",
};

const SUIT_LABELS = {
  action:      "ACTION",
  observation: "OBSERVATION",
  relation:    "RELATION",
};

const VZ = {
  ringW: 1.5, ringOpF: 0.55, ringOpB: 0.18,
  trailW: 1.4, trailOp: 0.6,
  bodySz: 1.0, halo: 1.0,
  forceW: 1.0, forceOp: 1.0,
  wireOp: 0.08,
};

function hexRgba(hex, a) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export function createRenderer(canvas, view) {
  const ctx = canvas.getContext("2d");
  let W = 0, H = 0, CX = 0, CY = 0, R = 0;

  function resize() {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.parentElement.getBoundingClientRect();
    W = rect.width;
    H = rect.height;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    CX = W / 2;
    CY = H / 2;
    R = Math.min(W, H) * 0.38;
  }

  function project(p) {
    const cy = Math.cos(view.yaw),   sy = Math.sin(view.yaw);
    const cx = Math.cos(view.pitch), sx = Math.sin(view.pitch);
    const x1 =  p.x * cy + p.z * sy;
    const y1 =  p.y;
    const z1 = -p.x * sy + p.z * cy;
    const x2 = x1;
    const y2 = y1 * cx - z1 * sx;
    const z2 = y1 * sx + z1 * cx;
    const d = 3.0;
    const k = d / (d + z2);
    return { x: CX + x2 * R * k, y: CY + y2 * R * k, z: z2, k };
  }

  function drawOutline() {
    ctx.beginPath();
    ctx.arc(CX, CY, R, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(243,243,243,${VZ.wireOp.toFixed(3)})`;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  function drawScaffold() {
    const lats = 5;
    for (let i = 1; i < lats; i++) {
      const lat = -Math.PI / 2 + (i / lats) * Math.PI;
      const y = Math.sin(lat), rr = Math.cos(lat);
      const N = 48;
      ctx.beginPath();
      for (let j = 0; j <= N; j++) {
        const a = (j / N) * Math.PI * 2;
        const p = project({ x: rr * Math.cos(a), y, z: rr * Math.sin(a) });
        if (j === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
      }
      ctx.strokeStyle = `rgba(243,243,243,${(VZ.wireOp * 0.5).toFixed(3)})`;
      ctx.lineWidth = 0.6;
      ctx.setLineDash([2, 7]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  function drawGreatCircle(m, key) {
    const axis = m.axis;
    const color = SUIT_COLORS[key];
    const N = 96;
    const TWO = Math.PI * 2;
    const mPhi = ((m.phi % TWO) + TWO) % TWO;
    const dir = m.effSpeed >= 0 ? 1 : -1;
    const tailFrac = 0.7;

    for (let i = 0; i < N; i++) {
      const a1 = (i / N) * TWO;
      const a2 = ((i + 1) / N) * TWO;
      const aMid = (a1 + a2) / 2;

      let behind = dir > 0 ? (mPhi - aMid) : (aMid - mPhi);
      behind = ((behind % TWO) + TWO) % TWO;
      const t = behind / TWO;
      if (t > tailFrac) continue;

      const k = 1 - t / tailFrac;
      const intensity = k * k * (3 - 2 * k);

      const p3a = axis === "x" ? { x: 0, y: Math.cos(a1), z: Math.sin(a1) }
                : axis === "y" ? { x: Math.cos(a1), y: 0, z: Math.sin(a1) }
                :                { x: Math.cos(a1), y: Math.sin(a1), z: 0 };
      const p3b = axis === "x" ? { x: 0, y: Math.cos(a2), z: Math.sin(a2) }
                : axis === "y" ? { x: Math.cos(a2), y: 0, z: Math.sin(a2) }
                :                { x: Math.cos(a2), y: Math.sin(a2), z: 0 };

      const p1 = project(p3a);
      const p2 = project(p3b);
      const isBack = (p1.z + p2.z) / 2 > 0;

      const baseOp = isBack ? VZ.ringOpB : VZ.ringOpF;
      const baseW  = isBack ? Math.max(0.5, VZ.ringW * 0.7) : VZ.ringW;

      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.strokeStyle = hexRgba(color, intensity * baseOp);
      ctx.lineWidth = baseW * Math.max(0.5, intensity);
      ctx.stroke();
    }
  }

  function drawTrail(trail) {
    if (trail.length < 2) return;
    const pts = trail.map(project);
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1], b = pts[i];
      const op = (i / pts.length) * VZ.trailOp;
      const depth = Math.max(0.35, (b.k - 0.5) * 1.5);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = `rgba(243,243,243,${(op * depth).toFixed(3)})`;
      ctx.lineWidth = VZ.trailW * b.k;
      ctx.stroke();
    }
  }

  function arrowhead(x, y, ang, color, op) {
    const s = 6;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(ang);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-s, -s * 0.6);
    ctx.lineTo(-s,  s * 0.6);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.globalAlpha = op;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  function drawForceLine(m, key, tp, mp) {
    const push = m.effForce < 0;
    const mass = Math.abs(m.effForce);
    const op = Math.min(0.7, 0.15 + mass / 500) * VZ.forceOp;
    const w  = (0.7 + mass / 250) * VZ.forceW;
    const color = SUIT_COLORS[key];

    if (push) {
      const dx = tp.x - mp.x, dy = tp.y - mp.y;
      const d = Math.hypot(dx, dy);
      if (d < 1) return;
      const L = Math.min(60, mass / 3);
      const x2 = tp.x + (dx / d) * L, y2 = tp.y + (dy / d) * L;
      ctx.beginPath();
      ctx.moveTo(tp.x, tp.y); ctx.lineTo(x2, y2);
      ctx.strokeStyle = color;
      ctx.globalAlpha = op;
      ctx.lineWidth = w;
      ctx.setLineDash([3, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      arrowhead(x2, y2, Math.atan2(y2 - tp.y, x2 - tp.x), color, op);
    } else {
      ctx.beginPath();
      ctx.moveTo(tp.x, tp.y); ctx.lineTo(mp.x, mp.y);
      ctx.strokeStyle = color;
      ctx.globalAlpha = op;
      ctx.lineWidth = w;
      ctx.setLineDash([2, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }
  }

  function drawMagnet(m, key, p) {
    const color = SUIT_COLORS[key];
    const ampAbs = Math.abs(m.effForce);
    const push = m.effForce < 0;
    const base = (9 + Math.sqrt(ampAbs) * 0.7) * VZ.bodySz;
    const size = base * p.k;

    if (push) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, size + 5, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.6;
      ctx.lineWidth = 0.8;
      ctx.setLineDash([3, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    ctx.beginPath();
    ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.globalAlpha = push ? 0.65 : 1;
    ctx.fill();
    ctx.globalAlpha = 1;

    if (view.labels) {
      const off = size + 18;
      const a = Math.atan2(p.y - CY, p.x - CX);
      const lx = p.x + Math.cos(a) * off;
      const ly = p.y + Math.sin(a) * off;
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.9 * Math.max(0.55, p.k);
      ctx.font = `600 ${Math.round(11 * Math.max(0.85, p.k))}px 'Onest', system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(SUIT_LABELS[key] + (push ? " · push" : ""), lx, ly);
      ctx.globalAlpha = 1;
    }
  }

  function drawTimeBody(p) {
    const size = 8 * VZ.bodySz * p.k;

    ctx.beginPath();
    ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
    ctx.fillStyle = "#F3F3F3";
    ctx.fill();

    if (view.labels) {
      ctx.fillStyle = "#F3F3F3";
      ctx.font = `600 ${Math.round(11 * Math.max(0.85, p.k))}px 'Onest', system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("TIME", p.x, p.y - size - 12);
    }
  }

  function render(sim) {
    ctx.fillStyle = "#1B1B1B";
    ctx.fillRect(0, 0, W, H);

    drawOutline();
    drawScaffold();

    for (const [key, m] of Object.entries(sim.magnets)) drawGreatCircle(m, key);

    const tP = project(sim.T);
    const mProjections = [];
    for (const [key, m] of Object.entries(sim.magnets)) {
      mProjections.push({ key, m, p: project(magnetPos(m)) });
    }

    drawTrail(sim.trail);

    for (const { key, m, p } of mProjections) drawForceLine(m, key, tP, p);

    const items = mProjections.map((d) => ({ kind: "magnet", z: d.p.z, key: d.key, m: d.m, p: d.p }));
    items.push({ kind: "time", z: tP.z, p: tP });
    items.sort((a, b) => b.z - a.z);
    for (const it of items) {
      if (it.kind === "magnet") drawMagnet(it.m, it.key, it.p);
      else drawTimeBody(it.p);
    }
  }

  resize();
  window.addEventListener("resize", resize);

  return { render, resize };
}
